import React from 'react';
/**
 * PracticeSession full-page states — Loading, Error, Empty variants.
 *
 * Extracted from PracticeSession.tsx in s69 to reduce its size from 978→~680 lines.
 * All components are pure presentational (no state management).
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DjayAvatar, getDjayThinkingMessage } from '../../mascot';
// ── Loading Spinner (shared animation) ───────────────────────
function LoadingSpinner() {
  return <motion.div animate={{
    scale: [1, 1.05, 1],
    rotate: [0, 3, -3, 0]
  }} transition={{
    duration: 2.5,
    repeat: Infinity,
    ease: 'easeInOut'
  }} className="mx-auto mb-6" data-id="element-7115">
      {/* @djay-slot: practice-loading */}
      <DjayAvatar size="xl" emotion="thinking" data-id="element-7116" />
    </motion.div>;
}
// ── Loading State ────────────────────────────────────────────
export function SessionLoadingState({
  isAssessmentMode
}: {
  isAssessmentMode: boolean;
}) {
  return <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50" data-id="element-7117">
      <div className="text-center" data-id="element-7118">
        <LoadingSpinner data-id="element-7119" />
        <h3 className="text-2xl font-black text-slate-800 mb-3" data-id="element-7120">
          Even geduld...
        </h3>
        <p className="text-slate-500 font-medium text-lg" data-id="element-7121">
          {isAssessmentMode ? 'We maken je toets klaar! ✨' : getDjayThinkingMessage().text}
        </p>
      </div>
    </div>;
}
// ── Error State (generation failed) ──────────────────────────
export function SessionErrorState({
  isAssessmentMode,
  onExit
}: {
  isAssessmentMode: boolean;
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-7122">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-7123">
        <div className="text-6xl mb-6" data-id="element-7124">😕</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-7125">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-7126">
          {isAssessmentMode ? 'De toets kon niet worden geladen.' : 'De oefensessie kon niet worden geladen.'}
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-7127">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty: No Content Available ──────────────────────────────
export function SessionEmptyNoContent({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-7128">
      <div className="max-w-lg w-full p-8 sm:p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-100 border-2 border-indigo-100" data-id="element-7129">
        <div className="flex justify-center mb-6" data-id="element-7130">
          <DjayAvatar size="xl" emotion="thinking" data-id="element-7131" />
        </div>
        <h2 className="text-3xl sm:text-4xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-7132">
          Hier zijn we nog mee bezig!
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-7133">
          Er zijn op dit moment nog geen vragen beschikbaar voor dit onderwerp.
          Probeer een ander vak — dan komen hier snel nieuwe vragen bij!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-xl shadow-indigo-200" data-id="element-7134">
          Kies een ander vak
        </Button>
      </div>
    </div>;
}
// ── Empty: All Mastered ──────────────────────────────────────
export function SessionEmptyAllMastered({
  selectedGroup,
  onExit
}: {
  selectedGroup: number | string;
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-7135">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-100 border-2 border-indigo-100" data-id="element-7136">
        <motion.div animate={{
        scale: [1, 1.1, 1],
        rotate: [0, 5, -5, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="text-8xl mb-6 drop-shadow-lg" data-id="element-7137">
          🌟
        </motion.div>
        <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-7138">
          Geweldig gedaan!
        </h2>
        <p className="text-slate-600 mb-8 text-xl leading-relaxed font-medium" data-id="element-7139">
          Je hebt alle vragen voor dit vak in groep {selectedGroup} super goed
          geoefend! 🎉
        </p>
        <p className="text-amber-600 font-bold text-lg mb-10" data-id="element-7140">
          Probeer een ander vak of kom later terug voor meer uitdaging!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-7141">
          <Sparkles className="w-6 h-6 mr-2 fill-current" data-id="element-7142" />
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Empty: Fallback (no progress, shouldn't happen) ──────────
export function SessionEmptyFallback({
  isAssessmentMode,
  onExit
}: {
  isAssessmentMode: boolean;
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-7143">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-7144">
        <div className="text-6xl mb-6" data-id="element-7145">🤔</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-7146">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-7147">
          {isAssessmentMode ? 'De toets kon niet worden gemaakt.' : 'We konden geen oefensessie maken.'}{' '}
          Probeer het nog eens!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-7148">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Safety Error (question undefined but session has questions) ─
export function SessionSafetyError({
  onExit
}: {
  onExit: () => void;
}) {
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-7149">
      <div className="max-w-lg w-full p-12 text-center bg-white rounded-[2.5rem] shadow-2xl shadow-rose-100 border-2 border-rose-100" data-id="element-7150">
        <div className="text-6xl mb-6" data-id="element-7151">🤔</div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight" data-id="element-7152">
          Er ging iets mis
        </h2>
        <p className="text-slate-600 mb-8 text-lg leading-relaxed font-medium" data-id="element-7153">
          We konden de vraag niet laden. Probeer het nog eens!
        </p>
        <Button onClick={onExit} variant="primary" size="xxl" fullWidth className="shadow-2xl shadow-indigo-200" data-id="element-7154">
          Terug naar overzicht
        </Button>
      </div>
    </div>;
}
// ── Exit Confirmation Modal (assessment mode) ────────────────
export function ExitConfirmModal({
  show,
  currentIndex,
  totalQuestions,
  onContinue,
  onStop
}: {
  show: boolean;
  currentIndex: number;
  totalQuestions: number;
  onContinue: () => void;
  onStop: () => void;
}) {
  return <AnimatePresence data-id="element-7155">
      {show && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50" onClick={onContinue} data-id="element-7156" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} transition={{
        type: 'spring',
        bounce: 0.2,
        duration: 0.4
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-7157">
            <Card className="max-w-md w-full p-8 shadow-2xl border-2 border-amber-100 bg-white rounded-3xl" data-id="element-7158">
              <div className="text-center" data-id="element-7159">
                <div className="text-5xl mb-4" data-id="element-7160">🤔</div>
                <h2 className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-7161">
                  Toets stoppen?
                </h2>
                <p className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-7162">
                  Weet je zeker dat je wilt stoppen? Je voortgang wordt
                  opgeslagen als onvolledig.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left" data-id="element-7163">
                  <div className="flex items-start gap-3" data-id="element-7164">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-7165" />
                    <p className="text-sm text-amber-800 font-medium" data-id="element-7166">
                      Je hebt {currentIndex + 1} van de {totalQuestions} vragen
                      beantwoord. Als je stopt, kun je later opnieuw beginnen.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3" data-id="element-7167">
                  <Button onClick={onContinue} variant="primary" size="lg" fullWidth data-id="element-7168">
                    Doorgaan met toets
                  </Button>
                  <Button onClick={onStop} variant="secondary" size="lg" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" data-id="element-7169">
                    Stoppen
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </>}
    </AnimatePresence>;
}